import { useState } from "react";
import { Link } from "react-router-dom";
import NavigationBar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { useSeo } from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";
import { siteInfo } from "@/utils/content";

export default function FaqPage() {
  const [open, setOpen] = useState<number | null>(0);

  useSeo({
    title: "常见问题 - 分类导航、更新频率与内容反馈 | 511爆料",
    description:
      "511爆料常见问题汇总：五大板块如何划分、怎样快速找到想看的内容、更新频率、能否转载与投稿，以及内容有疑问时的反馈方式。",
    keywords: "511爆料常见问题,511爆料分类导航,511爆料怎么用,511爆料投稿",
    canonical: "/faq",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.baseUrl },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${siteInfo.baseUrl}/faq` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <NavigationBar />
      <main className="flex-1">
        <PageHeader
          eyebrow="用户常见问题"
          title="关于 511爆料，你可能想问的"
          description="这里整理了读者最常提出的问题，包括板块划分、查找方式、更新频率、转载与投稿，以及如何反馈内容问题。"
          crumbs={[{ label: "常见问题" }]}
        />

        <section className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 md:px-8 md:py-14 lg:flex-row">
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-3">
              {faqs.map((item, index) => {
                const isOpen = open === index;
                return (
                  <div key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : index)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      aria-expanded={isOpen}
                    >
                      <span className="flex items-start gap-3">
                        <span className="font-heading text-sm font-bold text-primary-500">Q{index + 1}</span>
                        <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">{item.q}</span>
                      </span>
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-lg transition-colors ${
                          isOpen ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700"
                        }`}
                      >
                        <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} />
                      </span>
                    </button>
                    {isOpen && (
                      <p className="border-t border-background-200 px-5 py-4 pl-14 text-sm leading-relaxed text-foreground-600">
                        {item.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">还有其他问题？</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                如果你的问题不在上面，可以发邮件告诉我们。请在邮件里写清楚页面标题或链接，方便我们快速定位。
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-mail-send-line" />
                  发邮件提问
                </a>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  了解我们
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

          <aside className="flex w-full flex-col gap-6 lg:w-72">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground-950">快速直达</h2>
              <ul className="mt-4 flex flex-col gap-1 text-sm">
                <li>
                  <Link to="/category/all" className="flex items-center gap-2 rounded-md px-3 py-2.5 text-foreground-700 transition-colors hover:bg-background-100">
                    <i className="ri-layout-grid-line" />
                    全部爆料
                  </Link>
                </li>
                <li>
                  <Link to="/category/entertainment" className="flex items-center gap-2 rounded-md px-3 py-2.5 text-foreground-700 transition-colors hover:bg-background-100">
                    <i className="ri-movie-2-line" />
                    娱乐圈瓜
                  </Link>
                </li>
                <li>
                  <Link to="/category/workplace" className="flex items-center gap-2 rounded-md px-3 py-2.5 text-foreground-700 transition-colors hover:bg-background-100">
                    <i className="ri-briefcase-4-line" />
                    职场瓜
                  </Link>
                </li>
                <li>
                  <Link to="/category/local" className="flex items-center gap-2 rounded-md px-3 py-2.5 text-foreground-700 transition-colors hover:bg-background-100">
                    <i className="ri-map-pin-2-line" />
                    同城瓜
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}